import { db } from '../db';
import { config } from '../config';
import { metricsEngine } from './MetricsEngine';

/**
 * BusinessKPIs Service
 *
 * Fetches and processes strategic KPIs from the Hub/Admin sheet
 * for display in the Dashboard View.
 */
export const businessKPIs = {
    async getAllKPIs(timeframe = 'monthly') {
        try {
            const strategicKPIs = await this.fetchStrategicKPIs();
            const operationalMetrics = await metricsEngine.calculateMetrics(timeframe);

            return {
                ...strategicKPIs,
                ...operationalMetrics,
                lastUpdated: new Date().toISOString()
            };
        } catch (error) {
            console.error('Error fetching business KPIs:', error);
            throw error;
        }
    },

    async fetchStrategicKPIs() {
        try {
            const [
                financeExec,
                opsExec,
                salesExec,
                marketingExec,
                customerExec
            ] = await Promise.all([
                this.fetchAdminTab('Admin_Finance_Exec_Data'),
                this.fetchAdminTab('Admin_Ops_Exec_Data'),
                this.fetchAdminTab('Admin_Sales_Exec_Data'),
                this.fetchAdminTab('Admin_Marketing_Exec_Data'),
                this.fetchAdminTab('Admin_Customer_Exec_Data')
            ]);

            return {
                strategic: {
                    finance: this.processFinanceKPIs(financeExec),
                    operations: this.processOpsKPIs(opsExec),
                    sales: this.processSalesKPIs(salesExec),
                    marketing: this.processMarketingKPIs(marketingExec),
                    customer: this.processCustomerKPIs(customerExec)
                }
            };
        } catch (error) {
            console.error('Error fetching strategic KPIs:', error);
            return this.getDefaultStrategicKPIs();
        }
    },

    processFinanceKPIs(data) {
        if (!data || data.length === 0) {return this.getDefaultFinanceKPIs();}
        const latest = data.reduce((prev, current) => (new Date(prev.Date) > new Date(current.Date)) ? prev : current);
        return {
            revenueGrowth: parseFloat(latest.Revenue_Growth) || 0,
            profitMargin: parseFloat(latest.Profit_Margin) || 0,
            cashFlow: parseFloat(latest.Cash_Flow) || 0,
            runway: parseInt(latest.Runway_Months) || 0,
            budgetVariance: parseFloat(latest.Budget_Variance) || 0,
            arpu: parseFloat(latest.ARPU) || 0,
            customerLifetimeValue: parseFloat(latest.Customer_Lifetime_Value) || 0,
            customerAcquisitionCost: parseFloat(latest.Customer_Acquisition_Cost) || 0
        };
    },

    processOpsKPIs(data) {
        if (!data || data.length === 0) {return this.getDefaultOpsKPIs();}
        const latest = data.reduce((prev, current) => (new Date(prev.Date) > new Date(current.Date)) ? prev : current);
        return {
            operationalHealth: parseFloat(latest.Operational_Health_Score) || 0,
            systemUptime: parseFloat(latest.System_Uptime) || 0,
            processEfficiency: parseFloat(latest.Process_Efficiency) || 0,
            taskCompletionRate: parseFloat(latest.Task_Completion_Rate) || 0,
            onTimeDelivery: parseFloat(latest.On_Time_Delivery) || 0,
            qualityScore: parseFloat(latest.Quality_Score) || 0
        };
    },

    processSalesKPIs(data) {
        if (!data || data.length === 0) {return this.getDefaultSalesKPIs();}
        const latest = data.reduce((prev, current) => (new Date(prev.Date) > new Date(current.Date)) ? prev : current);
        return {
            salesGrowth: parseFloat(latest.Sales_Growth) || 0,
            conversionRate: parseFloat(latest.Conversion_Rate) || 0,
            averageDealSize: parseFloat(latest.Average_Deal_Size) || 0,
            salesCycleLength: parseFloat(latest.Sales_Cycle_Length) || 0,
            winRate: parseFloat(latest.Win_Rate) || 0,
            quotaAttainment: parseFloat(latest.Quota_Attainment) || 0
        };
    },

    processMarketingKPIs(data) {
        if (!data || data.length === 0) {return this.getDefaultMarketingKPIs();}
        const latest = data.reduce((prev, current) => (new Date(prev.Date) > new Date(current.Date)) ? prev : current);
        return {
            leadGeneration: parseFloat(latest.Lead_Generation) || 0,
            costPerLead: parseFloat(latest.Cost_Per_Lead) || 0,
            marketingROI: parseFloat(latest.Marketing_ROI) || 0,
            websiteTraffic: parseInt(latest.Website_Traffic) || 0,
            socialMediaEngagement: parseFloat(latest.Social_Media_Engagement) || 0,
            emailOpenRate: parseFloat(latest.Email_Open_Rate) || 0
        };
    },

    processCustomerKPIs(data) {
        if (!data || data.length === 0) {return this.getDefaultCustomerKPIs();}
        const latest = data.reduce((prev, current) => (new Date(prev.Date) > new Date(current.Date)) ? prev : current);
        return {
            customerSatisfaction: parseFloat(latest.Customer_Satisfaction) || 0,
            netPromoterScore: parseFloat(latest.Net_Promoter_Score) || 0,
            customerRetentionRate: parseFloat(latest.Customer_Retention_Rate) || 0,
            customerChurnRate: parseFloat(latest.Customer_Churn_Rate) || 0,
            supportResponseTime: parseFloat(latest.Support_Response_Time) || 0,
            supportResolutionTime: parseFloat(latest.Support_Resolution_Time) || 0
        };
    },

    async fetchAdminTab(tabName) {
        try {
            if (!db || !db.tabData) {
                console.warn(`BusinessKPIs: DB or tabData missing. Returning empty array for ${tabName}`);
                return [];
            }
            const entry = await db.tabData.where('tabName').equals(tabName).first();
            return entry?.data || [];
        } catch (e) {
            console.warn(`BusinessKPIs: Could not fetch ${tabName}`, e);
            return [];
        }
    },

    async getCachedKPIs() {
        try {
            // We can't easily cache the combined 'strategic' + 'operational' result in one table
            // because 'operational' is calculated on the fly.
            // However, we can return the most recent 'strategic' data from the DB to at least
            // fill the main cards immediately.

            const [
                financeExec,
                opsExec,
                salesExec,
                marketingExec,
                customerExec
            ] = await Promise.all([
                this.fetchAdminTab('Admin_Finance_Exec_Data'),
                this.fetchAdminTab('Admin_Ops_Exec_Data'),
                this.fetchAdminTab('Admin_Sales_Exec_Data'),
                this.fetchAdminTab('Admin_Marketing_Exec_Data'),
                this.fetchAdminTab('Admin_Customer_Exec_Data')
            ]);

            return {
                strategic: {
                    finance: this.processFinanceKPIs(financeExec),
                    operations: this.processOpsKPIs(opsExec),
                    sales: this.processSalesKPIs(salesExec),
                    marketing: this.processMarketingKPIs(marketingExec),
                    customer: this.processCustomerKPIs(customerExec)
                }
            };
        } catch (e) {
            console.error('BusinessKPIs: Error getting cached KPIs', e);
            return null;
        }
    },

    getDefaultStrategicKPIs() {
        return {
            strategic: {
                finance: this.getDefaultFinanceKPIs(),
                operations: this.getDefaultOpsKPIs(),
                sales: this.getDefaultSalesKPIs(),
                marketing: this.getDefaultMarketingKPIs(),
                customer: this.getDefaultCustomerKPIs()
            }
        };
    },

    getDefaultFinanceKPIs() { return { revenueGrowth: 0, profitMargin: 0, cashFlow: 0, runway: 0, budgetVariance: 0, arpu: 0, customerLifetimeValue: 0, customerAcquisitionCost: 0 }; },
    getDefaultOpsKPIs() { return { operationalHealth: 0, systemUptime: 0, processEfficiency: 0, taskCompletionRate: 0, onTimeDelivery: 0, qualityScore: 0 }; },
    getDefaultSalesKPIs() { return { salesGrowth: 0, conversionRate: 0, averageDealSize: 0, salesCycleLength: 0, winRate: 0, quotaAttainment: 0 }; },
    getDefaultMarketingKPIs() { return { leadGeneration: 0, costPerLead: 0, marketingROI: 0, websiteTraffic: 0, socialMediaEngagement: 0, emailOpenRate: 0 }; },
    getDefaultCustomerKPIs() { return { customerSatisfaction: 0, netPromoterScore: 0, customerRetentionRate: 0, customerChurnRate: 0, supportResponseTime: 0, supportResolutionTime: 0 }; }
};